import React, { useLayoutEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { get, isEmpty, isInteger } from 'lodash'
import { useParams } from 'react-router-dom'
import { useSetRecoilState } from 'recoil'
import { useSelector } from 'react-redux'
import {
  CREATE_AGG_FILE,
  CREATE_AGG_FILE_ADMIN,
  GET_AGG_REPORTS_BY_DI_CODE,
  GET_AGG_REPORTS_BY_DI_CODE_ADMIN,
} from '@landscape/gql'
import { Input, TagSet } from '@cb/apricot-react'
import { selectUserDiCode } from '@landscape/ui-redux'
import { formatDateString, CLIENT_TYPE } from '../Utils'
import { AggFileDownload } from './AggFileDownload'
import { addNotification, NotiType } from '../Notification'
import { notificationsState } from '../recoil-context'
import { OverlaySpinner } from '../OverlaySpinner'
import { Table } from '../Table'
import { useDashboardContext } from '../react-context'

interface AggReportProps {
  adminType: string
}

export const AggReport: React.FC<AggReportProps> = (props: any) => {
  const { adminType } = props
  const clientDiCode = useSelector(selectUserDiCode)
  const { requestedDiCode } = useParams() as { requestedDiCode: string }
  const di_code = adminType === CLIENT_TYPE.INTERNAL ? requestedDiCode : clientDiCode
  const dashboardReactContext = useDashboardContext()

  // decide which mutation to used based on if this is admin/dashboartd
  let gqlMutation = adminType === CLIENT_TYPE.INTERNAL ? CREATE_AGG_FILE_ADMIN : CREATE_AGG_FILE
  const [createAggFile] = useMutation(gqlMutation)
  const setNotis = useSetRecoilState(notificationsState)
  const ref = React.createRef<HTMLDivElement>()

  const aggFileColumns: any = [
    { label: 'Generation Date', field: 'createdDate', sortable: false, justify: 'start' },
    { label: 'Record Count', field: 'recordsCount', sortable: false, justify: 'end' },
    {
      label: 'Report Download Link',
      field: 'fileName',
      addnClasses: 'cb-glyph cb-download',
      extraAttrs: {
        'aria-hidden': 'true',
      },
      sortable: false,
      justify: 'start',
      customComponent: { comp: AggFileDownload, props: {} },
    },
  ]

  const [entryTerm, updateEntryTerm] = useState({
    validation: 'general',
    validationMsg: '',
  })

  const [entryTerms, updateEntryTerms] = useState([] as Array<number>)

  let getAggQuery =
    adminType === CLIENT_TYPE.INTERNAL
      ? GET_AGG_REPORTS_BY_DI_CODE_ADMIN
      : GET_AGG_REPORTS_BY_DI_CODE
  const { data, loading, error, refetch } = useQuery(getAggQuery, {
    variables: CLIENT_TYPE.INTERNAL? { input: { di_code } } : {},
    fetchPolicy: 'cache-and-network',
  })

  dashboardReactContext.refetchAggs = refetch
  const pathToData =
    adminType === CLIENT_TYPE.INTERNAL
      ? 'getAggReportByDiCodeAdmin.aggReports'
      : 'getAggReportByDiCodeExternalAdmin.aggReports'
  const aggReportsData = get(data, pathToData) ?? []
  const files: any = []

  aggReportsData.forEach((data: any) => {
    files.push({
      createdDate: formatDateString(data.createDate),
      fileName: data.fileName,
      fileKey: data.fileKey,
      di_code: data.di_code,
      fileSize: data.fileSize,
      recordsCount: data.recordsCount,
      id: data.aggFileID,
    })
  })

  useLayoutEffect(() => {
    document.getElementById('report')?.classList.remove('cb-open')
    document.title = 'Landscape - Aggregate Files'
  }, [])

  if (error) {
    return <div>Error! {error.message}</div>
  }

  const generateFileName = () => {
    const entryTermsJoin = entryTerms.join('-')
    return `${di_code}-aggregate-${entryTermsJoin}`
  }

  const handleGenerateAggFile = () => {
    let variables = { entryTerms }
    if (adminType == CLIENT_TYPE.INTERNAL) {
      variables['di_code'] = di_code
    }

    createAggFile({ variables })
    updateEntryTerms([])
    addNotification(
      {
        type: NotiType.INFO,
        message: `Aggregate file for ${entryTerms.join(
          ', '
        )} is now being processed. A notification message will appear once processing is complete.`,
        uniqueId: generateFileName(),
      },
      setNotis
    )
  }

  const handleAddEntryTerm = (event: any) => {
    if (entryTerm.validation === 'success') {
      const value = event.target.value
      if (!value) return
      const entryTerm = parseInt(value, 10)
      if (!entryTerms.includes(entryTerm)) {
        updateEntryTerms([...entryTerms, entryTerm].sort())
      }
      event.target.value = ''
      updateEntryTerm({
        validation: 'general',
        validationMsg: '',
      })
    }
  }
  const handleRemoveEntryTerm = (tagName: string, event: any) => {
    const entryTerm = parseInt(tagName, 10)
    updateEntryTerms(entryTerms.filter((item) => item !== entryTerm))
  }

  const validateEntryTerm = (event: any) => {
    const value = (event.target.value || '').trim()

    if (isEmpty(value)) {
      updateEntryTerm({
        validation: 'general',
        validationMsg: '',
      })
      return
    }

    const year = Number(value)
    if (isNaN(year) || !isInteger(year)) {
      updateEntryTerm({
        validation: 'error',
        validationMsg: 'Invalid year',
      })
      return
    }

    if (value.length < 4) {
      updateEntryTerm({
        validation: 'general',
        validationMsg: '',
      })
      return
    }

    if (year > new Date().getFullYear() + 10 || year < 1900) {
      updateEntryTerm({
        validation: 'error',
        validationMsg: 'Year out of range',
      })
    } else {
      updateEntryTerm({
        validation: 'success',
        validationMsg: 'Press Enter key',
      })
    }
  }

  return (
    <div className="container">
      <OverlaySpinner show={loading} id="agg-files-spinner" />

      <div className="cb-margin-bottom-48">
        <h4>Aggregate Files</h4>
        <p className="cb-margin-top-16">
          To generate an aggregate file of applicant records, enter desired entry term(s) in 4-digit
          year format.
        </p>
      </div>
      <div className="container cb-border cb-border-radius-8 cb-gutterv-24 cb-margin-bottom-32">
        <div className="row">
          <div className="col-xs-4 col-md-3">
            <Input
              id="entry-term-input"
              className="entry-term-flag"
              label="Entry Term"
              placeholder="YYYY"
              required="true"
              onKeyPress={handleAddEntryTerm}
              onChange={validateEntryTerm}
              onBlur={handleAddEntryTerm}
              validation={entryTerm.validation}
              validationMsg={entryTerm.validationMsg}
              ref={ref}
            />
          </div>
          <div className="col-xs-4 col-md-9" id="entry-terms-tagset">
            <TagSet
              icon="x-mark"
              label="Entry Terms"
              offScreen="Remove"
              onClick={handleRemoveEntryTerm}
              tagIcon="tag"
              tags={entryTerms}
            />
          </div>
        </div>
        <div className="row cb-margin-bottom-24">
          <div className="col-md-12" style={{ textAlign: 'center' }}>
            <button
              type="button"
              id="generate-agg-file-btn"
              className="cb-btn cb-btn-primary cb-btn-lg"
              disabled={isEmpty(entryTerms)}
              onClick={handleGenerateAggFile}
            >
              Generate Agg File
            </button>
          </div>
        </div>
      </div>

      {files.length > 0 && (
        <Table
          id="agg-report-table"
          data={files}
          columns={aggFileColumns}
          defaultSortField="reportCreatedDate"
          defaultSortAsc={false}
          condensed
          uniqueKeyColumn="fileName"
          pagination={files.length > 25}
          paginationRowPerPage={25}
          reduxTableName="aggReportsTable"
        />
      )}
    </div>
  )
}
